import React, { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import PortalShell from "@/components/portal/PortalShell";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Leaf, GraduationCap, FolderOpen, ClipboardList, Tags, Loader2, Layers, BookOpen,
  FileText, File, ImageIcon, Play, Check, Star, Share2, CalendarDays, Clock, Receipt,
  ArrowRight, Sparkles, CreditCard, CalendarPlus, Lock, Mail, Eye, EyeOff, X, Compass, ExternalLink,
} from "lucide-react";

const HERO_IMG = "https://customer-assets-lqy194kg.emergentagent.net/job_counsel-hub-106/artifacts/9wal6ako_Portal%20Image%20Apsect%20ratio%208%20to%209.png";
const inputCls = "mt-1.5 h-12 rounded-xl border-gold-200 bg-white/80 focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:border-transparent";

function AuthShell({ children }) {
  return (
    <div className="min-h-screen w-full bg-white lg:grid lg:grid-cols-2">
      <div className="flex min-h-screen items-center justify-center bg-white px-6 py-12 sm:px-12 lg:px-16 xl:px-24">
        <div className="w-full max-w-md">
          <div className="mb-10 flex items-center gap-3">
            <img src="/rcc-logo.png" alt="RCC Connect" className="h-11 w-auto" />
            <div>
              <div className="font-serif text-lg font-bold leading-tight text-navy-900">RCC Connect</div>
              <div className="text-xs text-navy-400">Your personal wellbeing and support space.</div>
            </div>
          </div>
          {children}
          <div className="mt-10 flex items-center gap-2 text-xs text-navy-400">
            <Mail className="h-3.5 w-3.5" /> hello@rcc-life.com.au
            <span className="ml-auto">© Resilience Community Collective</span>
          </div>
        </div>
      </div>
      <div className="relative hidden items-center justify-center bg-[#F6EEDD] lg:flex">
        <img src={HERO_IMG} alt="Welcome to your Resilience Account" className="h-full w-full object-cover" />
      </div>
    </div>
  );
}

function ClientLogin({ onLoggedIn }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const { data } = await api.post("/portal/login", { email, password }); onLoggedIn(data); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };
  const forgot = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/portal/forgot-password", { email }); setMode("sent"); }
    catch (err) { setMode("sent"); } finally { setLoading(false); }
  };

  if (mode === "sent") {
    return (
      <AuthShell>
        <div data-testid="portal-forgot-sent" className="text-center">
          <Mail className="mx-auto h-12 w-12 text-gold-500" />
          <h1 className="mt-6 font-serif text-3xl font-semibold text-navy-900">Check your email</h1>
          <p className="mt-3 text-navy-600">If an account exists for <span className="font-medium">{email}</span>, a reset link is on its way.</p>
          <button onClick={() => setMode("login")} className="mt-8 text-sm font-semibold text-gold-600 hover:text-gold-700">Back to sign in</button>
        </div>
      </AuthShell>
    );
  }
  if (mode === "forgot") {
    return (
      <AuthShell>
        <h1 className="font-serif text-4xl font-semibold tracking-tight text-navy-900">Forgot password</h1>
        <p className="mt-3 text-navy-600">Enter your email and we'll send you a secure reset link.</p>
        <form onSubmit={forgot} className="mt-8 space-y-5">
          <div>
            <Label className="text-navy-800">Email</Label>
            <Input data-testid="portal-forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
          </div>
          <Button data-testid="portal-forgot-submit" disabled={loading} className="h-12 w-full rounded-full bg-gold-500 font-semibold text-white shadow-ambient transition-transform duration-300 hover:-translate-y-0.5 hover:bg-gold-600">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
          </Button>
        </form>
        <button onClick={() => setMode("login")} className="mt-6 text-sm font-semibold text-gold-600 hover:text-gold-700">Back to sign in</button>
      </AuthShell>
    );
  }
  return (
    <AuthShell>
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold tracking-tight text-navy-900">Sign in</h1>
        <p className="mt-1.5 text-navy-500">Sign in to access your account.</p>
      </div>
      <form onSubmit={submit} className="space-y-5" data-testid="portal-login-form">
        <div>
          <Label className="text-navy-800">Email</Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-300" />
            <Input data-testid="portal-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className={inputCls + " pl-11"} required />
          </div>
        </div>
        <div>
          <Label className="text-navy-800">Password</Label>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-300" />
            <Input data-testid="portal-login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className={inputCls + " pl-11 pr-11"} required />
            <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Toggle password" className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-300 hover:text-navy-500">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-1.5 text-right">
            <button type="button" onClick={() => setMode("forgot")} data-testid="portal-forgot-link" className="text-sm font-medium text-gold-600 hover:text-gold-700">Reset password</button>
          </div>
        </div>
        {error && <div data-testid="portal-login-error" className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        <Button data-testid="portal-login-submit" disabled={loading} className="h-12 w-full rounded-full bg-gold-500 text-base font-semibold text-white shadow-ambient transition-transform duration-300 hover:-translate-y-0.5 hover:bg-gold-600">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign in <ArrowRight className="ml-1 h-4 w-4" /></>}
        </Button>
      </form>
      <p className="mt-8 text-center text-xs text-navy-400">New here? Your login details are emailed after you book your first session.</p>
    </AuthShell>
  );
}

function SetPasswordView({ onDone }) {
  const [pw, setPw] = useState(""); const [cf, setCf] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== cf) return setError("Passwords do not match.");
    setLoading(true);
    try { await api.post("/portal/set-password", { new_password: pw }); toast.success("Password set — welcome! 🌿"); await onDone(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <AuthShell>
      <h1 className="font-serif text-4xl font-semibold tracking-tight text-navy-900">Choose your password</h1>
      <p className="mt-3 text-navy-600">Set a password you'll use to sign in from now on.</p>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="portal-setpw-form">
        <div><Label className="text-navy-800">New password</Label><Input data-testid="portal-setpw-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className={inputCls} required /></div>
        <div><Label className="text-navy-800">Confirm password</Label><Input data-testid="portal-setpw-confirm" type="password" value={cf} onChange={(e) => setCf(e.target.value)} className={inputCls} required /></div>
        {error && <div className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        <Button data-testid="portal-setpw-submit" disabled={loading} className="h-12 w-full rounded-full bg-gold-500 font-semibold text-white shadow-ambient hover:-translate-y-0.5 hover:bg-gold-600 transition-transform duration-300">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save & continue"}
        </Button>
      </form>
    </AuthShell>
  );
}

function ResetView({ token, onDone }) {
  const [pw, setPw] = useState(""); const [cf, setCf] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== cf) return setError("Passwords do not match.");
    setLoading(true);
    try { await api.post("/portal/reset-password", { token, new_password: pw }); toast.success("Password updated — please sign in."); onDone(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <AuthShell>
      <h1 className="font-serif text-4xl font-semibold tracking-tight text-navy-900">Set a new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="portal-reset-form">
        <div><Label className="text-navy-800">New password</Label><Input data-testid="portal-reset-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} required /></div>
        <div><Label className="text-navy-800">Confirm password</Label><Input data-testid="portal-reset-confirm" type="password" value={cf} onChange={(e) => setCf(e.target.value)} className={inputCls} required /></div>
        {error && <div className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        <Button data-testid="portal-reset-submit" disabled={loading} className="h-12 w-full rounded-full bg-gold-500 font-semibold text-white shadow-ambient hover:-translate-y-0.5 hover:bg-gold-600 transition-transform duration-300">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
        </Button>
      </form>
    </AuthShell>
  );
}

const isAudio = (ct) => (ct || "").startsWith("audio");
const isVideo = (ct) => (ct || "").startsWith("video");
const isImage = (ct) => (ct || "").startsWith("image");

/* ---------- Lightbox (enlarge quote / image resources) ---------- */
function Lightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div data-testid="portal-lightbox" onClick={onClose} className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/85 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <button data-testid="portal-lightbox-close" onClick={onClose} aria-label="Close" className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-navy-900 shadow-ambient transition-transform duration-200 hover:scale-105 hover:bg-white">
        <X className="h-5 w-5" />
      </button>
      <img src={src} alt={alt || "Resource"} onClick={(e) => e.stopPropagation()} style={{ maxHeight: "88vh", maxWidth: "92vw", width: "auto", height: "auto" }} className="rounded-2xl object-contain shadow-2xl" />
    </div>
  );
}

const EmptyState = ({ icon: Icon, title, text }) => (
  <Card className="flex flex-col items-center justify-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-20 text-center shadow-none">
    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-50"><Icon className="h-8 w-8 text-gold-500" /></div>
    <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">{title}</h3>
    <p className="mt-2 max-w-sm text-navy-500">{text}</p>
  </Card>
);

/* ---------- Dashboard home (mirrors admin Dashboard) ---------- */
function DashboardTab({ user, content, onSelect }) {
  const firstName = (user?.name || "there").split(" ")[0];
  const [whatsNew, setWhatsNew] = useState([]);
  useEffect(() => { api.get("/portal/items?kind=whatsnew").then((r) => setWhatsNew(r.data || [])).catch(() => {}); }, []);
  return (
    <div data-testid="portal-dashboard-tab">
      <div className="mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-gold-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-700">
          <Leaf className="h-3.5 w-3.5" /> Your Resilience Journey
        </div>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl">
          Welcome back, {firstName} <span className="inline-block">🌿</span>
        </h1>
        <p className="mt-2 text-navy-500">Everything for your wellbeing journey, all in one calm space.</p>
      </div>

      <div className="relative overflow-hidden rounded-3xl border border-gold-100/70 bg-gradient-to-br from-[#FBF6EC] via-[#FBF6EC] to-[#F1E7D0] shadow-ambient">
        <img src="/leaf-corner.png" alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-[135%] w-auto -translate-y-1/2 select-none opacity-80 sm:-right-2" />
        <div className="relative flex h-56 flex-col justify-center p-8 sm:h-64 sm:p-12">
          <div className="mb-2 flex items-center gap-2 text-gold-600">
            <Leaf className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">My Wellbeing Journey</span>
          </div>
          <h2 className="font-serif text-4xl font-bold text-navy-900 sm:text-5xl">Owning My Journey</h2>
          <p className="mt-3 max-w-md text-lg text-navy-700">My wellbeing. My choices. My next step.</p>
          <button data-testid="portal-hero-cta" onClick={() => onSelect("courses")} className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-gold-500 px-6 py-2.5 text-sm font-semibold text-white shadow-ambient hover:bg-gold-600">
            Explore my courses <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-10">
        <div className="mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-gold-600" />
          <h2 className="font-serif text-2xl font-bold text-navy-900">What's New</h2>
        </div>
        {whatsNew.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-navy-100 bg-white/60 p-8 text-center shadow-none" data-testid="portal-whatsnew-empty">
            <p className="text-navy-500">Updates and new additions to your portal will appear here.</p>
          </Card>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {whatsNew.map((it) => {
              const Wrapper = it.link ? "a" : "div";
              const props = it.link ? { href: it.link, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Wrapper key={it.id} data-testid={`portal-whatsnew-${it.id}`} {...props} className="group block overflow-hidden rounded-2xl border border-navy-900/5 bg-white shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
                  {it.image && (
                    <div className="relative h-36 overflow-hidden bg-navy-50">
                      <img src={it.image} alt={it.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-serif text-lg font-bold text-navy-900">{it.title}</h3>
                    {it.description && <p className="mt-1 line-clamp-3 text-sm text-navy-500">{it.description}</p>}
                    {it.link && <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-gold-600">Learn more <ExternalLink className="h-3.5 w-3.5" /></span>}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="relative overflow-hidden rounded-3xl border border-gold-100/70 shadow-ambient lg:col-span-2">
          <img src="https://images.unsplash.com/photo-1533162507191-d90c625b2640?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" alt="Zen stones" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FBF6EC] via-[#FBF6EC]/85 to-[#FBF6EC]/10" />
          <div className="relative flex h-56 flex-col justify-center p-8">
            <div className="flex items-center gap-2 text-gold-600"><Sparkles className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-widest">Continue your journey</span></div>
            <h3 className="mt-2 font-serif text-2xl font-bold text-navy-900">A moment for you</h3>
            <p className="mt-2 max-w-sm text-navy-500">Take a wellbeing check-in or revisit a meditation whenever you need a gentle reset.</p>
            <button data-testid="portal-journey-cta" onClick={() => onSelect("checkins")} className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-navy-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-800">Start a check-in</button>
          </div>
        </div>
        <Card className="relative overflow-hidden rounded-3xl border border-gold-100 bg-gradient-to-br from-gold-50 via-white to-white p-8 shadow-ambient">
          <img src="/leaf-corner.png" alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 rotate-[18deg] select-none opacity-20" />
          <div className="relative">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-ambient"><CalendarDays className="h-5 w-5 text-gold-600" /></div>
            <h3 className="mt-5 font-serif text-xl font-bold text-navy-900">Your appointments</h3>
            <p className="mt-3 text-sm text-navy-500">See your upcoming counselling sessions.</p>
            <button data-testid="portal-appointments-cta" onClick={() => onSelect("appointments")} className="mt-5 w-full rounded-full bg-gold-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gold-600">View appointments</button>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ---------- Courses (admin card markup, read-only) ---------- */
function CoursesTab({ content }) {
  return (
    <div data-testid="portal-courses-tab">
      <PageHeader title="Courses" subtitle="Supportive learning journeys shared with you." />
      {content.courses.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No courses yet" text="Your courses will appear here as they're published." />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {content.courses.map((c) => (
            <Card key={c.id} className="group overflow-hidden rounded-2xl border-navy-900/5 bg-white shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <div className="relative h-40 overflow-hidden bg-navy-50">
                {c.thumbnail ? (
                  <img src={c.thumbnail} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full items-center justify-center"><GraduationCap className="h-10 w-10 text-navy-200" /></div>
                )}
              </div>
              <div className="p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-gold-600">{c.category}</div>
                <h3 className="mt-1 font-serif text-lg font-bold text-navy-900">{c.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-navy-500">{c.description || ""}</p>
                <div className="mt-3 flex items-center gap-4 text-xs text-navy-400">
                  <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {c.modules?.length || 0} modules</span>
                  <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {(c.modules || []).reduce((a, m) => a + (m.lessons?.length || 0), 0)} lessons</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Wellbeing Check-Ins (admin quiz card markup, read-only) ---------- */
function CheckinsTab({ content }) {
  return (
    <div data-testid="portal-checkins-tab">
      <PageHeader title="Wellbeing Check-Ins" subtitle="Self-reflection surveys to track how you're feeling. Your results stay private to you." />
      {content.quizzes.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No check-ins yet" text="Wellbeing check-ins shared with you will appear here." />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {content.quizzes.map((q) => (
            <Card key={q.id} className="rounded-2xl border-navy-900/5 bg-white p-6 shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50"><ClipboardList className="h-5 w-5 text-rose-500" /></div>
              <h3 className="mt-4 font-serif text-lg font-bold text-navy-900">{q.title}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-navy-500">{q.description || ""}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-navy-400">
                <span>{q.questions?.length || 0} questions</span>
                {q.share_results && <span className="flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> Results shared with you</span>}
              </div>
              {q.portal_link ? (
                <a href={q.portal_link} target="_blank" rel="noopener noreferrer" data-testid={`portal-checkin-link-${q.id}`} className="mt-4 block">
                  <Button className="w-full rounded-full bg-gold-500 text-white hover:bg-gold-600"><Play className="mr-1.5 h-3.5 w-3.5" /> Start check-in</Button>
                </a>
              ) : (
                <Button className="mt-4 w-full rounded-full bg-gold-500 text-white hover:bg-gold-600" data-testid={`portal-checkin-btn-${q.id}`}><Play className="mr-1.5 h-3.5 w-3.5" /> Start check-in</Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Resources (admin resource card markup + inline players, no download) ---------- */
function ResourceFile({ r, onZoom }) {
  const url = `${API}/resources/${r.id}/download`;
  const media = isAudio(r.content_type) || isVideo(r.content_type) || isImage(r.content_type);
  return (
    <Card className="flex flex-col rounded-2xl border-navy-900/5 bg-white p-5 shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
      <h3 className="font-semibold text-navy-900">{r.title}</h3>
      {r.description && <p className="mt-1 line-clamp-2 text-sm text-navy-500">{r.description}</p>}
      <div className="mt-3">
        {isImage(r.content_type) && (
          <button type="button" data-testid={`portal-resource-image-${r.id}`} onClick={() => onZoom(url, r.title)} className="group/z block w-full overflow-hidden rounded-xl">
            <img src={url} alt={r.title} className="w-full cursor-zoom-in rounded-xl transition-transform duration-300 group-hover/z:scale-[1.03]" />
            <span className="mt-2 flex items-center gap-1 text-xs font-medium text-gold-600">Tap to enlarge</span>
          </button>
        )}
        {isAudio(r.content_type) && <audio controls src={url} className="w-full" />}
        {isVideo(r.content_type) && <video controls src={url} className="w-full rounded-xl" />}
        {!media && <div className="rounded-lg bg-ivory px-3 py-2 text-xs text-navy-400">Viewable in your session with your counsellor.</div>}
      </div>
    </Card>
  );
}

function ResourcesTab({ content }) {
  const [openSection, setOpenSection] = useState(null);
  const [zoom, setZoom] = useState(null);
  const sections = content.sections || [];
  const onZoom = (src, alt) => setZoom({ src, alt });
  if (openSection) {
    return (
      <div data-testid="portal-resources-tab">
        {zoom && <Lightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />}
        <button onClick={() => setOpenSection(null)} data-testid="portal-section-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gold-600 hover:text-gold-700">
          <ArrowRight className="h-4 w-4 rotate-180" /> All resources
        </button>
        <PageHeader title={openSection.name} subtitle={openSection.description || "Files shared with you."} />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(openSection.resources || []).map((r) => <ResourceFile key={r.id} r={r} onZoom={onZoom} />)}
        </div>
      </div>
    );
  }
  if (!sections.length) {
    return (
      <div data-testid="portal-resources-tab">
        <PageHeader title="Resources" subtitle="Meditations, quotes and guides shared with you." />
        <EmptyState icon={FolderOpen} title="No resources yet" text="Meditations, quotes and files shared with you will appear here." />
      </div>
    );
  }
  return (
    <div data-testid="portal-resources-tab">
      {zoom && <Lightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />}
      <PageHeader title="Resources" subtitle="Explore meditations, quotes and guides shared with you." />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map((s) => (
          <button key={s.id} data-testid={`portal-section-${s.id}`} onClick={() => setOpenSection(s)} className="group text-left">
            <Card className="overflow-hidden rounded-2xl border-navy-900/5 bg-white shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
              <div className="relative h-40 overflow-hidden bg-gold-50">
                {s.thumbnail
                  ? <img src={s.thumbnail} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  : <div className="flex h-full items-center justify-center"><FolderOpen className="h-10 w-10 text-gold-300" /></div>}
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg font-bold text-navy-900">{s.name}</h3>
                {s.description && <p className="mt-1 line-clamp-2 text-sm text-navy-500">{s.description}</p>}
                <div className="mt-2 text-xs text-navy-400">{(s.resources || []).length} item{(s.resources || []).length === 1 ? "" : "s"}</div>
              </div>
            </Card>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Sessions & Packages (admin plan card markup, read-only) ---------- */
const intervals = { "one-time": "one-time", session: "per session", month: "per month", year: "per year" };
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pricingUrl, setPricingUrl] = useState("");
  useEffect(() => {
    api.get("/portal/content").then((r) => setPlans(r.data.plans || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/portal/pricing").then((r) => setPricingUrl(r.data.url || "")).catch(() => {});
  }, []);
  const buy = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/portal/checkout", { plan_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBusy(null);
    }
  };
  return (
    <div data-testid="portal-plans-tab">
      <PageHeader title="Sessions & Packages" subtitle="Book and pay for counselling sessions and packages securely." />
      {pricingUrl ? (
        <div className="overflow-hidden rounded-2xl border border-navy-900/5 bg-white shadow-ambient" data-testid="portal-pricing-embed">
          <iframe src={pricingUrl} title="Sessions & Packages" className="h-[1150px] w-full border-0" />
        </div>
      ) : loading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading…</div>
      ) : plans.length === 0 ? (
        <EmptyState icon={Tags} title="No packages yet" text="Session pricing and packages will appear here." />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => (
            <Card key={p.id} data-testid={`portal-plan-${p.id}`} className={`relative flex flex-col rounded-2xl p-6 shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg ${p.highlighted ? "border-2 border-gold-400 bg-white" : "border-navy-900/5 bg-white"}`}>
              {p.highlighted && <div className="absolute -top-3 left-6 flex items-center gap-1 rounded-full bg-gold-500 px-3 py-1 text-xs font-semibold text-white"><Star className="h-3 w-3" /> Most Popular</div>}
              <h3 className="font-serif text-xl font-bold text-navy-900">{p.name}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-bold text-navy-900">${p.price}</span>
                <span className="text-sm text-navy-400">{p.currency} · {intervals[p.interval] || p.interval}</span>
              </div>
              <p className="mt-2 text-sm text-navy-500">{p.description}</p>
              <ul className="mt-4 flex-1 space-y-2">
                {(p.features || []).map((f, i) => (
                  <li key={`${f}-${i}`} className="flex items-start gap-2 text-sm text-navy-700"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-600" /> {f}</li>
                ))}
              </ul>
              <Button data-testid={`buy-plan-${p.id}`} disabled={busy === p.id} onClick={() => buy(p)} className="mt-5 w-full rounded-full bg-gold-500 text-white hover:bg-gold-600">
                {busy === p.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                {p.price > 0 ? `Pay $${p.price} ${p.currency}` : "Get started"}
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Appointments (per-client, month calendar + GHL booking) ---------- */
function AppointmentsTab() {
  const [state, setState] = useState({ loading: true, events: [] });
  const [bookingUrl, setBookingUrl] = useState("");
  const [selected, setSelected] = useState(new Date());
  useEffect(() => {
    api.get("/portal/appointments").then((r) => setState({ loading: false, events: r.data.events || [] })).catch(() => setState({ loading: false, events: [] }));
    api.get("/portal/booking").then((r) => setBookingUrl(r.data.url || "")).catch(() => {});
  }, []);
  const parse = (e) => new Date(e.startTime || e.appointmentStartTime || e.dateAdded);
  const valid = state.events.filter((e) => !isNaN(parse(e)));
  const bookedDays = valid.map(parse);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const dayEvents = valid.filter((e) => sameDay(parse(e), selected));
  const upcoming = [...valid].sort((a, b) => parse(a) - parse(b));
  const book = () => { if (bookingUrl) window.open(bookingUrl, "_blank", "noopener"); };
  return (
    <div data-testid="portal-appointments-tab">
      <PageHeader
        title="Appointments"
        subtitle="Your upcoming counselling sessions — only ever your own."
        action={bookingUrl ? (
          <Button data-testid="book-session-button" onClick={book} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600 shadow-ambient">
            <CalendarPlus className="mr-2 h-4 w-4" /> Book a session
          </Button>
        ) : null}
      />
      {state.loading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading your appointments…</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="rounded-2xl border-navy-900/5 bg-white p-4 shadow-ambient">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => d && setSelected(d)}
              modifiers={{ booked: bookedDays }}
              modifiersClassNames={{ booked: "bg-gold-100 text-gold-800 font-bold rounded-full" }}
              className="mx-auto"
            />
            <p className="mt-2 px-2 text-center text-xs text-navy-400">Days with a session are highlighted in gold.</p>
          </Card>
          <div>
            <h3 className="mb-3 font-serif text-lg font-bold text-navy-900">
              {selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </h3>
            {dayEvents.length > 0 ? (
              <div className="space-y-3">
                {dayEvents.map((e, i) => (
                  <Card key={e.id || i} data-testid={`portal-appointment-${i}`} className="flex items-center gap-4 rounded-2xl border-navy-900/5 bg-white p-4 shadow-ambient">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold-50"><CalendarDays className="h-5 w-5 text-gold-600" /></div>
                    <div className="flex-1">
                      <div className="font-semibold text-navy-900">{e.title || "Counselling Session"}</div>
                      <div className="mt-1 flex items-center gap-1 text-sm text-navy-500"><Clock className="h-3.5 w-3.5" /> {parse(e).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    {e.appointmentStatus && <Badge className="border-0 bg-navy-50 text-navy-700">{e.appointmentStatus}</Badge>}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="rounded-2xl border-dashed border-navy-100 bg-white/60 p-6 text-sm text-navy-400 shadow-none">No sessions on this day.</Card>
            )}
            {upcoming.length > 0 && (
              <div className="mt-6">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-widest text-navy-400">All upcoming</h4>
                <div className="space-y-2">
                  {upcoming.slice(0, 6).map((e, i) => (
                    <button key={e.id || i} onClick={() => setSelected(parse(e))} className="flex w-full items-center justify-between rounded-xl bg-white px-4 py-2.5 text-left text-sm shadow-ambient hover:bg-gold-50">
                      <span className="font-medium text-navy-800">{e.title || "Counselling Session"}</span>
                      <span className="text-navy-400">{parse(e).toLocaleDateString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {valid.length === 0 && (
              <Card className="mt-4 flex flex-col items-center rounded-2xl border-dashed border-navy-100 bg-white/60 py-12 text-center shadow-none">
                <CalendarDays className="h-8 w-8 text-gold-500" />
                <p className="mt-3 max-w-xs text-sm text-navy-500">No upcoming appointments yet. {bookingUrl ? "Use \u201cBook a session\u201d to schedule one." : ""}</p>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Invoices (per-client, admin table markup) ---------- */
const statusColor = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "paid") return "bg-emerald-500 text-white";
  if (v === "sent" || v === "due") return "bg-gold-500 text-white";
  return "bg-navy-50 text-navy-700";
};
function InvoicesTab() {
  const [state, setState] = useState({ loading: true, invoices: [] });
  useEffect(() => {
    api.get("/portal/invoices").then((r) => setState({ loading: false, invoices: r.data.invoices || [] })).catch(() => setState({ loading: false, invoices: [] }));
  }, []);
  return (
    <div data-testid="portal-invoices-tab">
      <PageHeader title="Invoices" subtitle="Your personal invoices and payments, kept private to your account." />
      {state.loading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading your invoices…</div>
      ) : state.invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" text="Your invoices and payment records will appear here, visible only to you." />
      ) : (
        <Card className="overflow-hidden rounded-2xl border-navy-900/5 bg-white shadow-ambient">
          <Table>
            <TableHeader>
              <TableRow className="bg-ivory hover:bg-ivory">
                <TableHead className="text-navy-500">Invoice</TableHead>
                <TableHead className="text-navy-500">Amount</TableHead>
                <TableHead className="text-navy-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.invoices.map((inv, i) => (
                <TableRow key={inv._id || inv.id || i} data-testid={`portal-invoice-row-${i}`}>
                  <TableCell className="font-medium text-navy-900">{inv.invoiceNumber || inv.name || `#${i + 1}`}</TableCell>
                  <TableCell className="text-navy-900">{inv.currency || "$"} {inv.total ?? inv.amountDue ?? "—"}</TableCell>
                  <TableCell><Badge className={`border-0 ${statusColor(inv.status)}`}>{inv.status || "—"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

/* ---------- Discover More (admin-managed product tiles) ---------- */
function DiscoverTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/portal/items?kind=discover").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="portal-discover-tab">
      <PageHeader title="Discover More" subtitle="Explore more from the Resilience Community Collective." />
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>
      ) : items.length === 0 ? (
        <EmptyState icon={Compass} title="Nothing here yet" text="More products and offerings will appear here soon." />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => {
            const Wrapper = it.link ? "a" : "div";
            const props = it.link ? { href: it.link, target: "_blank", rel: "noopener noreferrer" } : {};
            return (
              <Wrapper key={it.id} data-testid={`portal-discover-${it.id}`} {...props} className="group block overflow-hidden rounded-2xl border border-navy-900/5 bg-white shadow-ambient transition-all duration-300 hover:-translate-y-1 hover:shadow-ambient-lg">
                <div className="relative h-44 overflow-hidden bg-navy-50">
                  {it.image ? <img src={it.image} alt={it.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center"><Compass className="h-10 w-10 text-navy-200" /></div>}
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-lg font-bold text-navy-900">{it.title}</h3>
                  {it.description && <p className="mt-1 line-clamp-3 text-sm text-navy-500">{it.description}</p>}
                  {it.link && <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-gold-600">Learn more <ExternalLink className="h-3.5 w-3.5" /></span>}
                </div>
              </Wrapper>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Portal() {
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [content, setContent] = useState({ courses: [], resources: [], quizzes: [], plans: [] });
  const [tab, setTab] = useState("dashboard");
  const [resetToken, setResetToken] = useState("");

  const load = async () => {
    const c = await api.get("/portal/content");
    setContent(c.data);
    setStatus("ready");
  };
  const logout = async () => {
    try { await api.post("/portal/logout"); } catch (e) {}
    setUser(null);
    setStatus("login");
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pay = params.get("payment");
    const sid = params.get("session_id");
    if (!pay) return;
    if (pay === "cancel") {
      toast.info("Payment cancelled — you can try again anytime.");
      window.history.replaceState({}, "", "/portal");
      return;
    }
    if (pay === "success" && sid) {
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("Payment successful — thank you! 🌿");
            window.history.replaceState({}, "", "/portal");
            return;
          }
        } catch (e) { /* keep polling */ }
        if (tries < 6) setTimeout(poll, 2000);
        else {
          toast.info("We're still confirming your payment — it'll update shortly.");
          window.history.replaceState({}, "", "/portal");
        }
      };
      poll();
    }
  }, []);

  const loadContent = load;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get("reset");
    if (rt) { setResetToken(rt); setStatus("reset"); return; }
    api.get("/portal/me").then(async (r) => {
      setUser(r.data);
      if (r.data.must_change) { setStatus("setpw"); }
      else { await load(); }
    }).catch(() => setStatus("login"));
  }, []);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <Loader2 className="h-8 w-8 animate-spin text-gold-500" />
      </div>
    );
  }
  if (status === "login") return <ClientLogin onLoggedIn={(u) => { setUser(u); if (u.must_change) setStatus("setpw"); else load(); }} />;
  if (status === "reset") return <ResetView token={resetToken} onDone={() => { window.history.replaceState({}, "", "/portal"); setStatus("login"); }} />;
  if (status === "setpw") return <SetPasswordView onDone={async () => { await load(); }} />;

  return (
    <PortalShell user={user} active={tab} onSelect={setTab} onLogout={logout}>
      {tab === "dashboard" && <DashboardTab user={user} content={content} onSelect={setTab} />}
      {tab === "courses" && <CoursesTab content={content} />}
      {tab === "checkins" && <CheckinsTab content={content} />}
      {tab === "resources" && <ResourcesTab content={content} />}
      {tab === "plans" && <PlansTab />}
      {tab === "appointments" && <AppointmentsTab />}
      {tab === "invoices" && <InvoicesTab />}
      {tab === "discover" && <DiscoverTab />}
    </PortalShell>
  );
}
